/**
 * Elevations for a room's FREE-STANDING cabinetry — an island or a peninsula.
 *
 * Every elevation surface in the app draws `groupWalls(room.walls)`, so an
 * island can never appear in one: it is not a wall. It is a cabinet-run
 * SEGMENT whose backing wall is synthesized (`segment.freestanding`, or a
 * `kind: 'island' | 'peninsula'` segment resolving to an inline
 * `cabinetLayout.cabinetWalls` entry). Both forms are exposed on BOTH long
 * faces, and both faces are real elevations a builder needs.
 *
 * This module mints those two elevations per leg, as ordinary rectangular
 * wall objects the existing renderer already understands — so nothing
 * downstream needs an island branch. `elevationWallGroups` is the one entry
 * point: it is `groupWalls` plus these, and it is what every elevation host
 * calls.
 *
 * **Side A / Side B, not "cook side" / "seating side".** The floor plan
 * already names a leg's two faces that way (`wallHandlesUtils`
 * .drawFreeSegmentDatum), for the reason spelled out there: `cookSide` is a
 * GUESS on most legs, so naming the faces by position lets the plan and the
 * elevation agree on WHICH face without either asserting what it is for.
 * Side A is the `cookSide` face — the same one the plan labels Side A.
 *
 * Pure. No I/O, no React.
 */

import * as THREE from 'three';
import { groupWalls } from './elevationViewerHelpers';
import {
  objectDimensionsMeters,
  withDescendants,
} from './elevationWallObjects';
import { resolveFreeSegment } from './cabinetRunGeometry';
import { getWallAxes } from '@shared-lib/kitchen-assembly/wallMath';
import { buildSegmentWallResolver } from '@shared-lib/cabinet-runs/segmentWall';
import { freeSegIndexOf } from '@shared-lib/cabinet-runs';
import { getFloorY } from '@shared-lib/vanity-assembly/placementUtils';
import { runAnswersTo } from '@shared-lib/cabinet-runs/runIds';
import { objectWallIds } from '@shared-lib/cabinet-runs/objectWallIds';

/** Segment kinds whose wall is synthesized rather than a room wall. */
const VIRTUAL_KINDS = new Set(['island', 'peninsula']);

/** Fallback ceiling height (m) when the room's walls carry none. */
const DEFAULT_WALL_HEIGHT = 2.4;

/** Nominal thickness for the synthesized elevation plane (m). */
const ELEVATION_PLANE_THICKNESS = 0.02;

/** A leg with nothing on it is not an elevation; below this, skip it. */
const MIN_LEG_LENGTH = 0.15;

/** Degenerate wall data must not collapse the drawing frame to nothing. */
const MIN_FRAME_HEIGHT = 0.5;

/**
 * The vertical frame the room's own walls stand in: where their floor line is
 * and how tall the tallest of them is.
 *
 * BOTH numbers are read, and the floor one is the load-bearing half. A scan's
 * room is not seated at y = 0 — this corpus's kitchens sit around y = −1.39 —
 * and a wall's `center.y` is its MID-height in that frame. Placing the face at
 * `height / 2` put it ~2.9 m above the island, so every object failed
 * `objectsNearWall`'s vertical span test and the elevation drew its frame with
 * nothing in it. Reading the frame off the walls also means the island shares
 * their scale, rather than being cropped to its own 36".
 *
 * @returns {{floorY: number, height: number}}
 */
function roomWallFrame(room) {
  let bottom = null;
  let top = null;
  (room?.walls || []).forEach((wall) => {
    const dims = wall?.dimensions;
    const h = Array.isArray(dims) ? dims[1] : dims?.y;
    const cy = wall?.center?.y;
    if (typeof h !== 'number' || h <= 0 || typeof cy !== 'number') return;
    const b = cy - h / 2;
    bottom = bottom == null ? b : Math.min(bottom, b);
    top = top == null ? cy + h / 2 : Math.max(top, cy + h / 2);
  });
  if (bottom == null) {
    return { floorY: getFloorY(room), height: DEFAULT_WALL_HEIGHT };
  }
  return { floorY: bottom, height: Math.max(top - bottom, MIN_FRAME_HEIGHT) };
}

/** Rotation object (radians) whose local +Z is `normal`. */
function rotationFacing(normal) {
  return { x: 0, y: Math.atan2(normal.x, normal.z), z: 0 };
}

/** Object centre as {x, y, z}, whatever shape it is stored in. */
function centerOf(obj) {
  const c = obj?.center;
  if (Array.isArray(c)) return { x: c[0] || 0, y: c[1] || 0, z: c[2] || 0 };
  if (c && typeof c === 'object')
    return { x: c.x || 0, y: c.y || 0, z: c.z || 0 };
  return null;
}

/**
 * Half the object's ORIENTED footprint along `axis` — the same projection
 * `objectsNearWall` measures a wall clearance with, so the face plane this
 * places and the membership test that follows it agree.
 */
function halfExtentAlong(obj, axis) {
  const dims = objectDimensionsMeters(obj.dimensions);
  let rx = 0;
  let ry = 0;
  let rz = 0;
  const r = obj.rotation;
  if (Array.isArray(r)) [rx, ry, rz] = r;
  else if (r && typeof r === 'object') {
    rx = r.x || 0;
    ry = r.y || 0;
    rz = r.z || 0;
  }
  if (Math.max(Math.abs(rx), Math.abs(ry), Math.abs(rz)) > 2 * Math.PI) {
    rx = THREE.MathUtils.degToRad(rx);
    ry = THREE.MathUtils.degToRad(ry);
    rz = THREE.MathUtils.degToRad(rz);
  }
  const e = new THREE.Euler(rx, ry, rz);
  const ax = new THREE.Vector3(1, 0, 0).applyEuler(e);
  const ay = new THREE.Vector3(0, 1, 0).applyEuler(e);
  const az = new THREE.Vector3(0, 0, 1).applyEuler(e);
  return (
    Math.abs(ax.dot(axis)) * (dims.width / 2) +
    Math.abs(ay.dot(axis)) * (dims.height / 2) +
    Math.abs(az.dot(axis)) * (dims.depth / 2)
  );
}

/**
 * The objects a given leg emitted or carries.
 *
 * A hand-drawn free-standing leg answers by run id and its own leg index. A
 * generated island / peninsula cannot: since a U or L kitchen became ONE run
 * (2026-09-14) the leg is a segment of the same run its wall legs are on, so
 * "the run's objects" is the whole kitchen's cabinetry — Girdwood's peninsula
 * claimed the far wall's uppers and hood, its body ran the depth of the
 * kitchen on the minimap, and its Side A elevation drew that hood.
 *
 * PARENTAGE answers it, in the two fields a room actually persists
 * (`objectWallIds`): the engine's boxes name the leg's cabinet wall in
 * `parentWallIds`; the generator's dishwasher, sink, seating overhang and
 * back panel name it in `cabinetWallRefs` and carry no runId at all. A
 * counter names nobody but its boxes, as `parents`, so ownership descends.
 */
function objectsOnLeg(room, run, seg, segIndex, isFreestanding) {
  const objects = room?.objects || [];
  const ownRun = (obj) => !obj?.runId || runAnswersTo(run, obj.runId);
  if (isFreestanding) {
    return objects.filter((obj) => {
      if (!obj?.runId || !ownRun(obj)) return false;
      const idx = freeSegIndexOf(obj, run);
      return idx == null || idx === segIndex;
    });
  }
  const wallId = seg?.wallId;
  if (!wallId) return [];
  const owned = new Set(
    objects
      .filter((obj) => ownRun(obj) && objectWallIds(obj).includes(wallId))
      .map((obj) => obj.id)
  );
  withDescendants(owned, objects);
  return objects.filter((obj) => owned.has(obj.id));
}

/**
 * Where the leg's two drawn faces actually are, as signed offsets from the
 * segment's own line along its inward normal.
 *
 * Measured off the leg's OBJECTS rather than off `segment.depth`, because the
 * line is not the island's centreline — `dividerLine` puts it at the front
 * row's back face — and the front/back split that would place it differs by
 * producer (a hand-drawn leg carries one depth and no split at all). The
 * objects answer it in one rule for every producer, and they carry the
 * countertop overhang, which is the edge a person actually stands at.
 *
 * Returns null when the leg has emitted nothing: an empty leg has no
 * elevation worth a page.
 */
function faceOffsets(legObjects, origin, inward) {
  let maxA = null;
  let minB = null;
  legObjects.forEach((obj) => {
    const c = centerOf(obj);
    if (!c) return;
    const along = (c.x - origin.x) * inward.x + (c.z - origin.z) * inward.z;
    const half = halfExtentAlong(obj, new THREE.Vector3(inward.x, 0, inward.z));
    maxA = maxA == null ? along + half : Math.max(maxA, along + half);
    minB = minB == null ? along - half : Math.min(minB, along - half);
  });
  return maxA == null ? null : { a: maxA, b: minB };
}

/**
 * One face's elevation wall: a rectangle standing on the leg's line, offset
 * to that face, facing OUT toward whoever is looking at it — the same sense a
 * room wall's normal has (it points at the viewer), which is what
 * `getProjectionView` reads to pick each object's drawn face.
 *
 * `offset` is signed along `inward` for BOTH faces — it comes straight out of
 * `faceOffsets`, which measures both in that one frame — while `facing` is
 * the side's own outward direction. Displacing along `facing` instead would
 * put Side B on top of Side A: its offset is already negative.
 */
function faceWall({
  axes,
  inward,
  offsets,
  offset,
  facing,
  frame,
  roomId,
  id,
  label,
  ref,
  legObjectIds,
  cabinetWallId,
}) {
  const mid = {
    x: (axes.start.x + axes.end.x) / 2 + inward.x * offset,
    z: (axes.start.z + axes.end.z) / 2 + inward.z * offset,
  };
  const slide = (p, d) => ({ x: p.x + inward.x * d, z: p.z + inward.z * d });
  return {
    id,
    roomId,
    label,
    // Named for the preview strip and every host that titles a page: without
    // it they fall back to a cardinal direction, and "North" for one face of
    // an island tells the reader nothing about which face it is.
    elevationLabel: label,
    // Seated on the room's own floor line, not on y = 0 — see roomWallFrame.
    center: { x: mid.x, y: frame.floorY + frame.height / 2, z: mid.z },
    dimensions: {
      x: axes.wallLen,
      y: frame.height,
      z: ELEVATION_PLANE_THICKNESS,
    },
    rotation: rotationFacing(facing),
    isVirtual: true,
    // The renderer draws a floor line instead of a wall rectangle for these:
    // an island is not backed by a wall, and a full-height box around one
    // reads as one.
    isFreestandingElevation: true,
    // Enough for the minimap to draw the island itself and aim its arrow at
    // the right face, without re-deriving a leg's geometry from the run —
    // `faceOffsets` above is the only place that is worked out. World space;
    // the minimap applies its own transform.
    freeSegment: {
      ...ref,
      // The leg's body, corners in order, Side A's edge then Side B's.
      footprint: [
        slide(axes.start, offsets.a),
        slide(axes.end, offsets.a),
        slide(axes.end, offsets.b),
        slide(axes.start, offsets.b),
      ],
      // The face THIS elevation is taken from, and the direction the reader
      // stands off in to look at it.
      face: {
        start: slide(axes.start, offset),
        end: slide(axes.end, offset),
        outward: { x: facing.x, z: facing.z },
      },
      // What the leg's own RUN emitted, and the cabinet wall its objects
      // name. `objectsNearWall` needs both to tell the leg's cabinetry from
      // the wall run standing in the same corner — position cannot, since a
      // leg's segment reaches the room wall.
      legObjectIds,
      cabinetWallId,
      // The leg's OWN LINE, and the direction its Side A lies in. This is the
      // line everything on the leg was placed against, and `dividerLine` puts
      // it at the front row's back face — so the SIGN of an object's offset
      // from it is the side it was put on: cabinets, sink, dishwasher and
      // faucet positive; back panel, seating overhang and stools negative.
      line: {
        origin: { x: axes.start.x, z: axes.start.z },
        normal: { x: inward.x, z: inward.z },
      },
      // This face's HALF of the leg — its own edge back to the leg's
      // centreline. The minimap picks a face by this region rather than by
      // the face line: the two lines are a leg's depth apart, which on a map
      // is a few pixels, so a line target makes choosing a side a coin toss.
      half: [
        slide(axes.start, offset),
        slide(axes.end, offset),
        slide(axes.end, (offsets.a + offsets.b) / 2),
        slide(axes.start, (offsets.a + offsets.b) / 2),
      ],
    },
  };
}

/**
 * Every free-standing leg in `room`, as a pair of elevation walls.
 *
 * Appended AFTER the room's own walls by `elevationWallGroups`, so the
 * existing wall indices — which two standalone pages address by number in
 * their URL — do not move.
 *
 * @param {Object} room room doc (`walls`, `objects`, `cabinetRuns`)
 * @returns {Object[]} elevation walls, two per leg that has objects on it
 */
export function freestandingElevationWalls(room) {
  const runs = room?.cabinetRuns || [];
  if (!runs.length) return [];

  const frame = roomWallFrame(room);
  // Room walls carry `roomId` (useElevationsData stamps it); these must too,
  // or nothing downstream can tell which floor a leg is on — the minimap
  // scopes what it marks by exactly this.
  const roomId = room?.id;
  let segmentResolver = null;
  const legs = [];

  runs.forEach((run) => {
    (run?.segments || []).forEach((seg, segIndex) => {
      const isFreestanding = !!seg?.freestanding;
      if (!isFreestanding && !VIRTUAL_KINDS.has(seg?.kind)) return;

      let wall;
      if (isFreestanding) {
        wall = resolveFreeSegment(run, segIndex, room)?.wall;
      } else {
        segmentResolver = segmentResolver || buildSegmentWallResolver(room);
        wall = segmentResolver.resolve(seg);
      }
      const axes = wall ? getWallAxes(wall) : null;
      if (!axes || axes.wallLen < MIN_LEG_LENGTH) return;

      const legObjects = objectsOnLeg(room, run, seg, segIndex, isFreestanding);
      const offsets = faceOffsets(legObjects, axes.start, axes.wallNormal);
      if (!offsets) return;

      legs.push({
        run,
        seg,
        segIndex,
        axes,
        offsets,
        legObjectIds: legObjects.map((obj) => obj.id).filter(Boolean),
        // The inline cabinet wall a virtual leg's objects name in their own
        // `cabinetWallRefs`. A hand-drawn freestanding leg has none.
        cabinetWallId: isFreestanding ? null : seg?.wallId || null,
      });
    });
  });

  // Number the legs only when there is more than one, exactly as the preview
  // strip numbers duplicate cardinal directions.
  const kindCounts = {};
  legs.forEach((leg) => {
    const kind = leg.seg?.kind === 'peninsula' ? 'Peninsula' : 'Island';
    kindCounts[kind] = (kindCounts[kind] || 0) + 1;
  });
  const kindIndices = {};

  const walls = [];
  legs.forEach(
    ({ run, seg, segIndex, axes, offsets, legObjectIds, cabinetWallId }) => {
      const kind = seg?.kind === 'peninsula' ? 'Peninsula' : 'Island';
      kindIndices[kind] = (kindIndices[kind] || 0) + 1;
      const name =
        kindCounts[kind] > 1 ? `${kind} ${kindIndices[kind]}` : `${kind}`;
      const inward = new THREE.Vector3(
        axes.wallNormal.x,
        0,
        axes.wallNormal.z
      ).normalize();
      const baseId = `${run.id || 'run'}__seg${segIndex}`;

      walls.push(
        faceWall({
          axes,
          inward,
          offsets,
          offset: offsets.a,
          facing: inward,
          frame,
          roomId,
          id: `${baseId}__sideA`,
          label: `${name} Side A`,
          ref: { runId: run.id, segIndex, side: 'A' },
          legObjectIds,
          cabinetWallId,
        }),
        faceWall({
          axes,
          inward,
          offsets,
          offset: offsets.b,
          facing: inward.clone().negate(),
          frame,
          roomId,
          id: `${baseId}__sideB`,
          label: `${name} Side B`,
          ref: { runId: run.id, segIndex, side: 'B' },
          legObjectIds,
          cabinetWallId,
        })
      );
    }
  );

  return walls;
}

/**
 * THE elevation list for a room: its grouped walls, then its free-standing
 * legs' faces. Every host that draws elevations calls this instead of
 * `groupWalls` directly, so an island appears in the viewer, the room
 * package, and the exported PDFs alike.
 *
 * `walls` is passed separately rather than read off `room` because callers
 * decorate them first (interior widths, original indices) and the elevation
 * must be drawn from the decorated copies.
 */
export function elevationWallGroups(walls, room) {
  return [...groupWalls(walls || []), ...freestandingElevationWalls(room)];
}
